import type { Paragraph } from '@/api/types/stowed-element'
import type { ChainFrameFit } from '@/lib/text/types/chain-frame-fit'
import type { ParagraphBoxFit } from '@/lib/text/types/paragraph-box-fit'
import {
  mergeParagraphAtSeam,
  paragraphsHaveText,
} from '@/lib/text-linking/merge-overflow-paragraphs'
import { keysMatch } from './paragraphs-key'

/** True when a single paragraph carries at least one character. */
export function paragraphHasText(paragraph: Paragraph | undefined): boolean {
  return paragraph != null && paragraphsHaveText([paragraph])
}

/**
 * Joins two paragraph lists, merging the last block of `upstream` with the first block of
 * `downstream` so the two halves read as one continuous line.
 *
 * There is no choice to make about whose formatting survives, and since Phase D no parameter offering
 * one either: a fused seam is by definition two halves of ONE source paragraph, so the merged
 * paragraph keeps that paragraph's own properties (D6). Formatting travels with the text rather than
 * being overwritten by whichever frame currently displays it.
 */
export function fuseLists(upstream: Paragraph[], downstream: Paragraph[]): Paragraph[] {
  return [
    ...upstream.slice(0, -1),
    mergeParagraphAtSeam(upstream[upstream.length - 1]!, downstream[0]!),
    ...downstream.slice(1),
  ]
}

/**
 * Was the boundary between `upstream` and `downstream` a mid-paragraph character split rather than a
 * paragraph the user ended with Enter?
 *
 * Nothing on the wire records it, so re-derive it: re-fit the fused candidate into the box of the
 * frame that held the upstream half. If the seam really was a character split AND upstream is still
 * at (or over) capacity, that candidate is the original pre-split story and the fit reproduces the
 * split exactly. A candidate that was never split instead loses its whole seam block (`kept` stops
 * matching) or overflows a whole block (`overflowIsFragment` false).
 *
 * **When upstream has slack, `null` (fits whole) now means FUSE, not "separate" (WMPCM-10397).**
 * This reverses the function's original assumption, which read `null` as proof the two halves were
 * always independent — reasonable in isolation, but wrong for what this module's header calls "one
 * continuous story": once two blocks of text sit in a linked chain, this cross-frame probe is the
 * ONLY place that ever decides whether they read as one line or two, and it is the ONLY thing
 * standing between "the user is typing into a chain" and "every keystroke that lands in a frame with
 * room upstream mints a new permanent one-character paragraph, forever" — the exact defect this
 * ticket reports, confirmed by direct reproduction against `distributeStory`. A frame with slack
 * cannot overflow no matter which way the boundary should read, so the old test could never even
 * ask the question in that case; treating "fits whole, nothing is lost" as fusable answers it the
 * way the one-story model already promises to.
 *
 * **Residual risk, accepted as the lesser defect (mirrors the reasoning in WMPCM-9542's Escape
 * decision).** A genuine paragraph break the user typed lives inside ONE frame's own paragraph list
 * and is never examined by this probe at all — `storyFromChain` never re-fuses paragraphs already
 * co-resident in a single frame, only across a frame boundary (see that function). So this can only
 * misfire in the narrow, transient case where the user's Enter falls exactly at a CURRENT chain-frame
 * boundary at the moment it is probed — and redistribution recomputes that boundary from capacity on
 * every subsequent edit, so the window in which a real Enter could sit exactly there is one edit
 * wide. Silently and permanently fragmenting every frame with slack is the more damaging, and far
 * more common, failure to guard against.
 *
 * **Probed both ways when it does overflow.** This is a re-derivation, so it must reproduce
 * whichever fitter produced the seam. A seam created after WMPCM-9070's 2026-08-24 fix was verified
 * against the renderer (`verifyKeptFits`) and reproduces only under that flag; every seam already
 * sitting in a saved spread predates it and reproduces only without. Nothing distinguishes them, so
 * accept either.
 *
 * `fillToCharacter` is not optional: the seam being probed was produced by a frame that HAD a
 * downstream half — that is what made it a seam — so it was split character-accurately.
 *
 * Residual limit, accepted rather than probed away: a fuse can still be admitted where the seam was
 * a real paragraph boundary AND the upstream frame's last row had no horizontal room for another
 * character. The alternative — a spurious mid-sentence break on every genuine mid-paragraph seam —
 * is the worse defect (WMPCM-6996).
 *
 * Lifted from the deleted `merge-chain-frame-content.ts`, where it ran once per edit on the delete
 * path. Here it
 * runs once per boundary per assembly, and only for seams this session did not itself create.
 */
export function seamWasCharacterSplit(
  upstream: Paragraph[],
  downstream: Paragraph[],
  fitUpstream: ChainFrameFit
): boolean {
  const fused = fuseLists(upstream, downstream)
  const reproduces = (probe: ParagraphBoxFit | null): boolean =>
    probe != null &&
    probe.overflowIsFragment &&
    keysMatch(probe.kept, upstream) &&
    keysMatch(probe.overflow, downstream)

  const verified = fitUpstream(fused, { fillToCharacter: true, verifyKeptFits: true })

  // Fits upstream whole: nothing overflows, so fusing loses no character either way — see the
  // WMPCM-10397 note above for why this reads as fusable rather than "definitely separate."
  if (verified === null) return true

  return reproduces(verified) || reproduces(fitUpstream(fused, { fillToCharacter: true }))
}
